import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../api.js";
import { Badge, Button, EmptyState, Eyebrow, Field, LoadingRows, Logo, Notice } from "../../components/ui.jsx";

export default function OnboardingPage({ onAuthenticated, onLogout }) {
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", domain: "" });
  const [acting, setActing] = useState("");

  const load = useCallback(async () => {
    try {
      const data = await api.onboarding();
      setState({ loading: false, data, error: "" });
      setForm((current) => ({ ...current, domain: current.domain || data.email_domain || "" }));
    } catch (error) {
      setState({ loading: false, data: null, error: error.message });
    }
  }, []);

  useEffect(() => { document.title = "Choose an organization · RevPal"; load(); }, [load]);

  const requests = useMemo(() => new Map(
    (state.data?.join_requests || []).map((request) => [request.workspace.id, request]),
  ), [state.data]);

  async function openWorkspace(workspaceId) {
    setActing(`open:${workspaceId}`);
    try {
      const data = await api.selectWorkspace(workspaceId);
      onAuthenticated(data.actor);
    } catch (error) {
      setState((current) => ({ ...current, error: error.message }));
      setActing("");
    }
  }

  async function requestAccess(workspaceId) {
    setActing(`request:${workspaceId}`);
    try {
      await api.requestWorkspaceJoin(workspaceId);
      await load();
    } catch (error) {
      setState((current) => ({ ...current, error: error.message }));
    } finally {
      setActing("");
    }
  }

  async function createWorkspace(event) {
    event.preventDefault();
    setActing("create");
    setState((current) => ({ ...current, error: "" }));
    try {
      const data = await api.createWorkspace(form);
      onAuthenticated(data.actor);
    } catch (error) {
      setState((current) => ({ ...current, error: error.message }));
      setActing("");
    }
  }

  async function logout() {
    try { await api.logout(); } finally { onLogout(); }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="mx-auto flex min-h-20 w-full max-w-[1440px] items-center justify-between border-b border-neutral-100 px-5 sm:px-8 lg:px-12"><a href="/" aria-label="RevPal home"><Logo /></a><Button variant="ghost" onClick={logout}>Sign out</Button></header>
      <main className="mx-auto grid w-full max-w-[1180px] gap-10 px-5 py-12 sm:px-8 lg:px-12 lg:py-20">
        <header className="grid gap-4 border-b border-border pb-9 sm:grid-cols-[1fr_auto] sm:items-end">
          <div className="grid gap-3"><Eyebrow>Organization access</Eyebrow><h1 className="max-w-[18ch] text-[clamp(2rem,3.6vw,3rem)] font-extrabold leading-[1.06] tracking-[-0.03em]">Where are you working today?</h1>{state.data && <p className="text-sm text-muted-foreground">Signed in as <strong className="text-neutral-600">{state.data.account.email}</strong></p>}</div>
          <span className="text-xs text-muted-foreground">Account first · Workspace second</span>
        </header>

        {state.error && <Notice>{state.error} {!state.data && <Button variant="ghost" className="ml-2" onClick={load}>Retry</Button>}</Notice>}
        {state.loading ? <LoadingRows count={5} /> : state.data && <>
          <section className="grid gap-4 rounded-xl bg-card p-6 shadow-soft">
            <div className="flex items-end justify-between gap-4"><div className="grid gap-1"><h2 className="text-xl font-bold">Your organizations</h2><p className="text-xs text-muted-foreground">Memberships you can open now.</p></div><Badge>{state.data.memberships.length}</Badge></div>
            {state.data.memberships.length ? <div>{state.data.memberships.map((membership) => <div className="grid min-h-[82px] gap-4 border-b border-neutral-100 py-4 last:border-b-0 last:pb-0 sm:grid-cols-[1fr_auto] sm:items-center" key={membership.membership_id}><div className="grid gap-1"><strong>{membership.workspace.name}</strong><span className="text-xs text-muted-foreground">{membership.workspace.domain || "Domain not recorded"} · {membership.role}</span></div><Button variant="primary" disabled={Boolean(acting)} onClick={() => openWorkspace(membership.workspace.id)}>{acting === `open:${membership.workspace.id}` ? "Opening…" : "Open workspace"}</Button></div>)}</div> : <EmptyState title="No organization memberships yet" description="Create a new organization below, or request access to an organization that matches your work-email domain." />}
          </section>

          <section className="grid gap-4 rounded-xl bg-card p-6 shadow-soft">
            <div className="grid gap-1"><h2 className="text-xl font-bold">Organizations using {state.data.email_domain}</h2><p className="text-xs text-muted-foreground">Domain matches are suggestions only. An owner must approve your request.</p></div>
            {state.data.organizations.length ? <div>{state.data.organizations.map((organization) => {
              const request = requests.get(organization.id);
              const pending = request?.status === "pending";
              return <div className="grid min-h-[82px] gap-4 border-b border-neutral-100 py-4 last:border-b-0 last:pb-0 sm:grid-cols-[1fr_auto] sm:items-center" key={organization.id}><div className="grid gap-1"><strong>{organization.name}</strong><span className="text-xs text-muted-foreground">{organization.domain}</span>{request && <Badge status={pending ? "running" : request.status === "approved" ? "active" : "revoked"}>{request.status}</Badge>}</div><Button disabled={Boolean(acting) || pending} onClick={() => requestAccess(organization.id)}>{pending ? "Approval pending" : acting === `request:${organization.id}` ? "Requesting…" : request?.status === "rejected" ? "Request again" : "Request access"}</Button></div>;
            })}</div> : <p className="py-2 text-sm leading-7 text-muted-foreground">No discoverable organization currently matches this email domain. Consumer-email domains are never used for organization discovery.</p>}
          </section>

          <section className="grid gap-5 rounded-xl bg-card p-6 shadow-soft">
            <div className="flex flex-wrap items-end justify-between gap-4"><div className="grid gap-1"><h2 className="text-xl font-bold">Create a new organization</h2><p className="text-xs text-muted-foreground">You become its owner and enter it immediately.</p></div>{!creating && <Button variant="primary" onClick={() => setCreating(true)}>Create organization</Button>}</div>
            {creating && <form className="grid gap-5 rounded-xl border border-border bg-muted p-6 sm:grid-cols-2" onSubmit={createWorkspace}><div className="col-span-full"><Eyebrow>New private workspace</Eyebrow></div><Field label="Organization name" placeholder="e.g. Northstar Revenue" value={form.name} maxLength={160} autoFocus required onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} /><Field label="Organization domain" placeholder="example.com" value={form.domain} maxLength={253} required onChange={(event) => setForm((current) => ({ ...current, domain: event.target.value }))} /><p className="col-span-full text-xs leading-5 text-muted-foreground">The domain helps colleagues discover this organization. It does not grant access; join requests still require approval.</p><div className="col-span-full flex justify-end gap-2"><Button onClick={() => setCreating(false)}>Cancel</Button><Button variant="primary" type="submit" disabled={Boolean(acting)}>{acting === "create" ? "Creating…" : "Create and open"}</Button></div></form>}
          </section>
        </>}
      </main>
    </div>
  );
}
